import { SupabaseClient } from '@supabase/supabase-js';

import { supabase, TABLES } from '../../config/supabase';
import {
  Account,
  AccountCreationData,
  AccountCreationSchema,
  AuthError,
  Holding,
  HoldingAnalysis,
  HoldingEntryData,
  HoldingEntrySchema,
  PortfolioSummary,
  SecurityInfo,
  TickerValidationResult,
} from '../../types';
import { CACHE_TTL, EnhancedCache } from '../../utils/cache';

// Database row interfaces
interface AccountRow {
  id: string;
  user_id: string;
  goal_id?: string;
  account_name: string;
  description?: string;
  account_type: string;
  plaid_account_id?: string;
  plaid_item_id?: string;
  bank_account_type?: string;
  institution_name?: string;
  account_mask?: string;
  current_balance?: number;
  available_balance?: number;
  currency: string;
  last_synced_at?: string;
  sync_enabled: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface HoldingRow {
  id: string;
  account_id: string;
  symbol: string;
  quantity: number;
  average_cost?: number;
  created_at: string;
  updated_at: string;
}

interface AccountUpdateFields {
  account_name?: string;
  account_type?: string;
  plaid_account_id?: string;
  updated_at: string;
}

interface HoldingUpdateFields {
  symbol?: string;
  quantity?: number;
  average_cost?: number;
  updated_at: string;
}

export class PortfolioService {
  private static instance: PortfolioService;
  private supabase: SupabaseClient;
  private cache: EnhancedCache;

  constructor(supabaseClient: SupabaseClient = supabase) {
    this.supabase = supabaseClient;
    this.cache = new EnhancedCache({
      defaultTTL: CACHE_TTL.MEDIUM,
      maxSize: 50,
    });
  }

  static getInstance(): PortfolioService {
    if (!PortfolioService.instance) {
      PortfolioService.instance = new PortfolioService();
    }
    return PortfolioService.instance;
  }

  // ============================================================================
  // Ticker Validation and Security Information
  // ============================================================================

  /**
   * Validates a ticker symbol and returns security information
   * Requirements: 4.3 - ticker validation and security information display
   */
  async validateTicker(ticker: string): Promise<TickerValidationResult> {
    try {
      const normalizedTicker = ticker.toUpperCase().trim();

      if (!normalizedTicker) {
        return {
          isValid: false,
          error: 'Ticker symbol is required',
        };
      }

      if (!/^[A-Z]{1,10}$/.test(normalizedTicker)) {
        return {
          isValid: false,
          error: 'Ticker must be 1-10 uppercase letters only',
        };
      }

      // TODO: Replace with real stock data API (Alpha Vantage, IEX Cloud, etc.)
      // For now, return invalid to indicate API integration needed
      return {
        isValid: false,
        error: 'Stock data API integration required',
      };
    } catch {
      return {
        isValid: false,
        error: 'Failed to validate ticker symbol',
      };
    }
  }

  /**
   * Gets security information for a ticker
   * Requirements: 4.3 - security information display
   */
  async getSecurityInfo(ticker: string): Promise<SecurityInfo | null> {
    const result = await this.validateTicker(ticker);
    return result.isValid ? result.security || null : null;
  }

  /**
   * Gets current price for a ticker
   * Requirements: 4.6 - portfolio calculations and performance metrics
   */
  async getCurrentPrice(ticker: string): Promise<number | null> {
    const security = await this.getSecurityInfo(ticker);
    return security?.price || null;
  }

  // ============================================================================
  // Account Management
  // ============================================================================

  /**
   * Creates a new account for a profile
   * Requirements: 4.4, 4.5 - account management for manual and Plaid-connected accounts
   */
  async createAccount(
    userId: string,
    accountData: AccountCreationData
  ): Promise<{ account?: Account; error?: AuthError }> {
    try {
      // Validate input data
      const validationResult = AccountCreationSchema.safeParse(accountData);
      if (!validationResult.success) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Invalid account data',
            details: validationResult.error.flatten(),
          },
        };
      }

      const accountRecord = {
        user_id: userId,
        account_type:
          accountData.accountType === 'brokerage'
            ? 'manual_portfolio'
            : accountData.accountType,
        account_name: accountData.accountName,
        plaid_account_id: accountData.plaidAccountId || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await this.supabase
        .from(TABLES.ACCOUNTS)
        .insert(accountRecord)
        .select()
        .single();

      if (error) {
        return {
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to create account',
            details: { originalError: error },
          },
        };
      }

      const account: Account = {
        id: data.id,
        userId: data.user_id,
        accountType: data.account_type,
        name: data.account_name,
        plaidAccountId: data.plaid_account_id,
        goalId: data.goal_id,
        description: data.description,
        currentBalance: data.current_balance,
        currency: data.currency,
        isActive: data.is_active,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };

      return { account };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: 'An unexpected error occurred while creating account',
          details: { originalError: error },
        },
      };
    }
  }

  /**
   * Gets all accounts for a profile
   * Requirements: 4.4, 4.5 - account management
   */
  async getAccountsByUser(
    userId: string
  ): Promise<{ accounts: Account[]; error?: AuthError }> {
    try {
      const { data, error } = await this.supabase
        .from(TABLES.ACCOUNTS)
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: true });

      if (error) {
        return {
          accounts: [],
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to fetch accounts',
            details: { originalError: error },
          },
        };
      }

      const accounts: Account[] = (data || []).map((row: AccountRow) => ({
        id: row.id,
        userId: row.user_id,
        goalId: row.goal_id,
        name: row.account_name,
        description: row.description,
        accountType: row.account_type as Account['accountType'],
        plaidAccountId: row.plaid_account_id,
        currentBalance: row.current_balance ?? 0,
        currency: row.currency,
        isActive: row.is_active,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      }));
      return { accounts };
    } catch (error) {
      return {
        accounts: [],
        error: {
          code: 'UNKNOWN_ERROR',
          message: 'An unexpected error occurred while fetching accounts',
          details: { originalError: error },
        },
      };
    }
  }

  /**
   * Updates an account
   * Requirements: 4.4, 4.5 - account management
   */
  async updateAccount(
    accountId: string,
    updates: Partial<AccountCreationData>
  ): Promise<{ account?: Account; error?: AuthError }> {
    try {
      const updateRecord: AccountUpdateFields = {
        updated_at: new Date().toISOString(),
      };

      if (updates.accountName !== undefined) {
        updateRecord.account_name = updates.accountName;
      }
      if (updates.accountType !== undefined) {
        updateRecord.account_type = updates.accountType;
      }
      if (updates.plaidAccountId !== undefined) {
        updateRecord.plaid_account_id = updates.plaidAccountId;
      }

      const { data, error } = await this.supabase
        .from(TABLES.ACCOUNTS)
        .update(updateRecord)
        .eq('id', accountId)
        .select()
        .single();

      if (error) {
        return {
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to update account',
            details: { originalError: error },
          },
        };
      }

      const account: Account = {
        id: data.id,
        userId: data.user_id,
        accountType: data.account_type,
        name: data.account_name,
        plaidAccountId: data.plaid_account_id,
        goalId: data.goal_id,
        description: data.description,
        currentBalance: data.current_balance,
        currency: data.currency,
        isActive: data.is_active,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };

      return { account };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: 'An unexpected error occurred while updating account',
          details: { originalError: error },
        },
      };
    }
  }

  /**
   * Deletes an account and all its holdings
   * Requirements: 4.4, 4.5 - account management
   */
  async deleteAccount(
    accountId: string
  ): Promise<{ success: boolean; error?: AuthError }> {
    try {
      // First delete all holdings for this account
      const { error: holdingsError } = await this.supabase
        .from(TABLES.HOLDINGS)
        .delete()
        .eq('account_id', accountId);

      if (holdingsError) {
        return {
          success: false,
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to delete account holdings',
            details: { originalError: holdingsError },
          },
        };
      }

      // Then delete the account
      const { error } = await this.supabase
        .from(TABLES.ACCOUNTS)
        .delete()
        .eq('id', accountId);

      if (error) {
        return {
          success: false,
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to delete account',
            details: { originalError: error },
          },
        };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: 'An unexpected error occurred while deleting account',
          details: { originalError: error },
        },
      };
    }
  }

  // ============================================================================
  // Holdings Management (CRUD Operations)
  // ============================================================================

  /**
   * Creates a new holding in an account
   * Requirements: 4.1, 4.2 - portfolio entry with ticker and quantity validation
   */
  async createHolding(
    accountId: string,
    holdingData: HoldingEntryData
  ): Promise<{ holding?: Holding; error?: AuthError }> {
    try {
      // Validate input data
      const validationResult = HoldingEntrySchema.safeParse(holdingData);
      if (!validationResult.success) {
        return {
          error: {
            code: 'VALIDATION_ERROR',
            message: 'Invalid holding data',
            details: validationResult.error.flatten(),
          },
        };
      }

      const validatedData = validationResult.data;

      // Validate ticker exists
      const tickerValidation = await this.validateTicker(validatedData.ticker);
      if (!tickerValidation.isValid) {
        return {
          error: {
            code: 'INVALID_TICKER',
            message: tickerValidation.error || 'Invalid ticker symbol',
          },
        };
      }

      const holdingRecord = {
        account_id: accountId,
        symbol: validatedData.ticker,
        quantity: validatedData.quantity,
        average_cost: validatedData.entryPrice || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await this.supabase
        .from(TABLES.HOLDINGS)
        .insert(holdingRecord)
        .select()
        .single();

      if (error) {
        return {
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to create holding',
            details: { originalError: error },
          },
        };
      }

      const holding: Holding = {
        id: data.id,
        accountId: data.account_id,
        symbol: data.symbol,
        quantity: data.quantity,
        averageCost: data.average_cost,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };

      return { holding };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: 'An unexpected error occurred while creating holding',
          details: { originalError: error },
        },
      };
    }
  }

  /**
   * Gets all holdings for an account
   * Requirements: 4.1, 4.6 - holdings display and management
   */
  async getHoldingsByAccount(
    accountId: string
  ): Promise<{ holdings: Holding[]; error?: AuthError }> {
    try {
      const { data, error } = await this.supabase
        .from(TABLES.HOLDINGS)
        .select('*')
        .eq('account_id', accountId)
        .order('created_at', { ascending: true });

      if (error) {
        return {
          holdings: [],
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to fetch holdings',
            details: { originalError: error },
          },
        };
      }

      const holdings: Holding[] = (data || []).map((row: HoldingRow) => ({
        id: row.id,
        accountId: row.account_id,
        symbol: row.symbol,
        quantity: row.quantity,
        averageCost: row.average_cost,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      }));

      return { holdings };
    } catch (error) {
      return {
        holdings: [],
        error: {
          code: 'UNKNOWN_ERROR',
          message: 'An unexpected error occurred while fetching holdings',
          details: { originalError: error },
        },
      };
    }
  }

  /**
   * Gets all holdings for a profile across all accounts
   * Requirements: 4.6 - portfolio calculations and performance metrics
   */
  async getHoldingsByUser(
    userId: string
  ): Promise<{ holdings: HoldingAnalysis[]; error?: AuthError }> {
    const cacheKey = `holdings-${userId}`;
    const cached = this.cache.get(cacheKey) as {
      holdings: HoldingAnalysis[];
      error?: AuthError;
    } | null;
    if (cached) {
      return cached;
    }

    try {
      // First get all account IDs for this user
      const { data: accounts, error: accountError } = await this.supabase
        .from(TABLES.ACCOUNTS)
        .select('id')
        .eq('user_id', userId)
        .eq('is_active', true);

      if (accountError) {
        return {
          holdings: [],
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to fetch user accounts',
            details: { originalError: accountError },
          },
        };
      }

      if (!accounts || accounts.length === 0) {
        const result = { holdings: [] };
        this.cache.set(cacheKey, result, CACHE_TTL.MEDIUM);
        return result;
      }

      const accountIds = accounts.map(account => account.id);

      // Get holdings for all user's accounts using the view
      const { data, error } = await this.supabase
        .from(TABLES.HOLDINGS_WITH_SECURITIES)
        .select('*')
        .in('account_id', accountIds)
        .order('created_at', { ascending: true });

      if (error) {
        return {
          holdings: [],
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to fetch holdings',
            details: { originalError: error },
          },
        };
      }

      const holdings: HoldingAnalysis[] = (data || []).map(row => ({
        id: row.id,
        accountId: row.account_id,
        symbol: row.symbol, // Use symbol directly
        quantity: row.quantity,
        averageCost: row.average_cost, // Use average_cost directly
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        // Additional HoldingAnalysis fields
        currentPrice: row.current_price
          ? parseFloat(row.current_price)
          : undefined,
        currentValue: row.market_value
          ? parseFloat(row.market_value)
          : undefined,
        gainLoss: row.unrealized_gain_loss
          ? parseFloat(row.unrealized_gain_loss)
          : undefined,
        gainLossPercent: row.unrealized_gain_loss_percentage
          ? parseFloat(row.unrealized_gain_loss_percentage)
          : undefined,
        dailyChangePercent: row.daily_change_percentage
          ? parseFloat(row.daily_change_percentage)
          : undefined,
        security: row.name
          ? {
              ticker: row.symbol,
              name: row.name,
              price: row.current_price
                ? parseFloat(row.current_price)
                : undefined,
              currency: row.currency || 'USD',
              exchange: row.exchange,
              type: row.asset_type as SecurityInfo['type'],
              logoUrl: row.logo_url,
            }
          : undefined,
      }));

      const result = { holdings };
      this.cache.set(cacheKey, result, CACHE_TTL.MEDIUM);
      return result;
    } catch (error) {
      return {
        holdings: [],
        error: {
          code: 'UNKNOWN_ERROR',
          message: 'An unexpected error occurred while fetching holdings',
          details: { originalError: error },
        },
      };
    }
  }

  /**
   * Updates a holding
   * Requirements: 4.1, 4.2 - holdings management with validation
   */
  async updateHolding(
    holdingId: string,
    updates: Partial<HoldingEntryData>
  ): Promise<{ holding?: Holding; error?: AuthError }> {
    try {
      const updateRecord: HoldingUpdateFields = {
        updated_at: new Date().toISOString(),
      };

      if (updates.ticker !== undefined) {
        // Validate ticker if being updated
        const tickerValidation = await this.validateTicker(updates.ticker);
        if (!tickerValidation.isValid) {
          return {
            error: {
              code: 'INVALID_TICKER',
              message: tickerValidation.error || 'Invalid ticker symbol',
            },
          };
        }
        updateRecord.symbol = updates.ticker.toUpperCase();
      }

      if (updates.quantity !== undefined) {
        if (updates.quantity <= 0) {
          return {
            error: {
              code: 'VALIDATION_ERROR',
              message: 'Quantity must be positive',
            },
          };
        }
        updateRecord.quantity = updates.quantity;
      }

      if (updates.entryPrice !== undefined) {
        updateRecord.average_cost = updates.entryPrice;
      }

      const { data, error } = await this.supabase
        .from(TABLES.HOLDINGS)
        .update(updateRecord)
        .eq('id', holdingId)
        .select()
        .single();

      if (error) {
        return {
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to update holding',
            details: { originalError: error },
          },
        };
      }

      const holding: Holding = {
        id: data.id,
        accountId: data.account_id,
        symbol: data.symbol,
        quantity: data.quantity,
        averageCost: data.average_cost,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };

      return { holding };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message: 'An unexpected error occurred while updating holding',
          details: { originalError: error },
        },
      };
    }
  }

  /**
   * Deletes a holding
   * Requirements: 4.1 - holdings management
   */
  async deleteHolding(
    holdingId: string
  ): Promise<{ success: boolean; error?: AuthError }> {
    try {
      const { error } = await this.supabase
        .from(TABLES.HOLDINGS)
        .delete()
        .eq('id', holdingId);

      if (error) {
        return {
          success: false,
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to delete holding',
            details: { originalError: error },
          },
        };
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'UNKNOWN_ERROR',
          message: 'An unexpected error occurred while deleting holding',
          details: { originalError: error },
        },
      };
    }
  }

  // ============================================================================
  // Portfolio Calculations and Performance Metrics
  // ============================================================================

  /**
   * Calculates portfolio summary with performance metrics
   * Requirements: 4.6 - portfolio calculations and performance metrics
   */
  async calculatePortfolioSummary(
    userId: string
  ): Promise<{ summary?: PortfolioSummary; error?: AuthError }> {
    try {
      // First get all active account IDs for this user
      const { data: accounts, error: accountError } = await this.supabase
        .from(TABLES.ACCOUNTS)
        .select('id')
        .eq('user_id', userId)
        .eq('is_active', true);

      if (accountError) {
        return {
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to fetch user accounts',
            details: { originalError: accountError },
          },
        };
      }

      if (!accounts || accounts.length === 0) {
        return {
          summary: {
            totalValue: 0,
            totalGainLoss: 0,
            totalGainLossPercent: 0,
            holdingsCount: 0,
            lastUpdated: new Date().toISOString(),
          },
        };
      }

      const accountIds = accounts.map(account => account.id);

      // Get the most recent account performance snapshots for these accounts
      const { data, error } = await this.supabase
        .from(TABLES.ACCOUNT_PERFORMANCE)
        .select('total_value, account_id')
        .in('account_id', accountIds)
        .order('snapshot_date', { ascending: false })
        .order('snapshot_time', { ascending: false });

      if (error) {
        return {
          error: {
            code: 'DATABASE_ERROR',
            message: 'Failed to fetch account performance data',
            details: { originalError: error },
          },
        };
      }

      if (!data || data.length === 0) {
        // No performance data available, return empty summary
        return {
          summary: {
            totalValue: 0,
            totalGainLoss: 0,
            totalGainLossPercent: 0,
            holdingsCount: 0,
            lastUpdated: new Date().toISOString(),
          },
        };
      }

      // Group by account and get the most recent snapshot for each
      const accountSnapshots = new Map<string, (typeof data)[0]>();
      for (const snapshot of data) {
        const accountId = snapshot.account_id;
        if (!accountSnapshots.has(accountId)) {
          accountSnapshots.set(accountId, snapshot);
        }
      }

      // Sum up values from the most recent snapshots
      let totalValue = 0;
      let holdingsCount = 0;

      for (const snapshot of accountSnapshots.values()) {
        totalValue += snapshot.total_value || 0;
        // Count accounts with any value as having holdings
        if (snapshot.total_value > 0) {
          holdingsCount++;
        }
      }

      const summary: PortfolioSummary = {
        totalValue,
        totalGainLoss: 0, // Simplified: no longer calculated
        totalGainLossPercent: 0, // Simplified: no longer calculated
        holdingsCount,
        lastUpdated: new Date().toISOString(),
      };

      return { summary };
    } catch (error) {
      return {
        error: {
          code: 'UNKNOWN_ERROR',
          message:
            'An unexpected error occurred while calculating portfolio summary',
          details: { originalError: error },
        },
      };
    }
  }

  /**
   * Gets detailed holding analysis with current prices and performance
   * Requirements: 4.6 - portfolio calculations and performance metrics
   */
  async getHoldingAnalysis(
    userId: string
  ): Promise<{ analysis: HoldingAnalysis[]; error?: AuthError }> {
    // Since getHoldingsByUser now returns HoldingAnalysis[] with security data,
    // we can just return that directly
    const result = await this.getHoldingsByUser(userId);
    return {
      analysis: result.holdings,
      error: result.error,
    };
  }
}

// Export singleton instance for backward compatibility
export const portfolioService = new PortfolioService();
